import { useCallback, useEffect, useRef, useState } from "react";
import { usePlayerStore } from "../stores/playerStore";
import { useAudioSettings } from "./useAudioSettings";
import { useElementSpacing } from "./useElementSpacing";
import { useHapticEngine } from "./useHapticsEngine";
import { DEFAULT_PRESETS } from "./useHapticsEngine/types";

// Singleton audio element to prevent multiple instances
let globalAudioElement: HTMLAudioElement | null = null;

type EffectNodes = {
  lowpass: BiquadFilterNode;
  highpass: BiquadFilterNode;
  reverb: ConvolverNode;
  distortion: WaveShaperNode;
  compression: DynamicsCompressorNode;
  delay: DelayNode;
};

export const useAudioPlayer = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null);
  const equalizerFiltersRef = useRef<BiquadFilterNode[]>([]);
  const effectNodesRef = useRef<EffectNodes>({} as EffectNodes);
  const visualizerAnalyserRef = useRef<AnalyserNode | null>(null);

  // Race condition prevention
  const [isAudioContextInitialized, setIsAudioContextInitialized] = useState(false);
  const [isSettingUpTrack, setIsSettingUpTrack] = useState(false);
  const [audioContextError, setAudioContextError] = useState<Error | null>(null);
  const currentTrackIdRef = useRef<string | null>(null);
  const volumeSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    setProgress,
    setCurrentTime,
    setIsPlaying,
    playNext,
    currentTime,
    isPlaying,
    currentTrack,
    volume,
    setVolume,
    setVisualizerAnalyserNode,
  } = usePlayerStore();

  const { analyzeTrack, provideFeedback } = useHapticEngine(audioContextRef.current);
  const { equalizer, effects, volume: savedVolume } = useAudioSettings(() => {});

  // Initialize singleton audio element
  useEffect(() => {
    if (!globalAudioElement) {
      globalAudioElement = new Audio();
      globalAudioElement.preload = "metadata";
      console.log("=== useAudioPlayer: Created singleton audio element");
    }
    audioRef.current = globalAudioElement;
  }, []);

  // Debounced volume save
  const saveVolumeDebounced = useCallback((vol: number) => {
    if (volumeSaveTimeoutRef.current) {
      clearTimeout(volumeSaveTimeoutRef.current);
    }
    volumeSaveTimeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem("audioVolume", vol.toString());
      } catch (error) {
        console.warn("Failed to save volume:", error);
      }
    }, 300);
  }, []);

  // Animate volume changes smoothly
  const animateVolume = useCallback((targetVolume: number, duration: number, onComplete?: () => void) => {
    if (!audioRef.current) return;

    const audio = audioRef.current;
    const startVolume = audio.volume;
    const startTime = performance.now();

    const frame = (currentTime: number) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      audio.volume = startVolume + (targetVolume - startVolume) * progress;

      if (progress < 1) {
        requestAnimationFrame(frame);
      } else {
        if (onComplete) onComplete();
      }
    };
    requestAnimationFrame(frame);
  }, []);

  // Initialize Audio Context and Web Audio API
  useEffect(() => {
    const initializeAudioContext = async () => {
      if (audioContextError) return;

      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
          console.log("=== useAudioPlayer: Created AudioContext");
        }

        // Resume context if suspended
        if (audioContextRef.current.state === "suspended") {
          await audioContextRef.current.resume();
          console.log("=== useAudioPlayer: AudioContext resumed");
        }

        if (!sourceNodeRef.current && audioContextRef.current && audioRef.current) {
          sourceNodeRef.current = audioContextRef.current.createMediaElementSource(audioRef.current);
          console.log("=== useAudioPlayer: Created MediaElementSource");

          // Create equalizer filters
          const frequencies = [32, 64, 125, 250, 500, 1000, 2000, 4000, 8000, 16000];
          equalizerFiltersRef.current = frequencies.map((freq) => {
            const filter = audioContextRef.current!.createBiquadFilter();
            filter.type = "peaking";
            filter.frequency.value = freq;
            filter.Q.value = 1;
            filter.gain.value = 0;
            return filter;
          });

          // Create effect nodes
          effectNodesRef.current = {
            lowpass: audioContextRef.current.createBiquadFilter(),
            highpass: audioContextRef.current.createBiquadFilter(),
            reverb: audioContextRef.current.createConvolver(),
            distortion: audioContextRef.current.createWaveShaper(),
            compression: audioContextRef.current.createDynamicsCompressor(),
            delay: audioContextRef.current.createDelay(),
          };

          // Setup effect node parameters
          effectNodesRef.current.lowpass.type = "lowpass";
          effectNodesRef.current.highpass.type = "highpass";

          // Create visualizer analyser
          visualizerAnalyserRef.current = audioContextRef.current.createAnalyser();
          visualizerAnalyserRef.current.fftSize = 2048;
          visualizerAnalyserRef.current.smoothingTimeConstant = 0.8;
          setVisualizerAnalyserNode(visualizerAnalyserRef.current);

          // Connect basic chain: source -> filters -> analyser -> destination
          let currentNode: AudioNode = sourceNodeRef.current;

          // Connect equalizer
          equalizerFiltersRef.current.forEach((filter) => {
            currentNode.connect(filter);
            currentNode = filter;
          });

          // Connect to analyser and destination
          currentNode.connect(visualizerAnalyserRef.current);
          visualizerAnalyserRef.current.connect(audioContextRef.current.destination);

          setIsAudioContextInitialized(true);
          console.log("=== useAudioPlayer: Audio chain initialized");
        }
      } catch (error) {
        console.error("=== useAudioPlayer: AudioContext initialization failed:", error);
        setAudioContextError(error as Error);
      }
    };

    initializeAudioContext();

    // Add user interaction listener for mobile browsers
    const handleInteraction = async () => {
      if (audioContextRef.current?.state === "suspended") {
        await audioContextRef.current.resume();
        console.log("=== useAudioPlayer: AudioContext resumed on user interaction");
      }
      document.removeEventListener("click", handleInteraction);
      document.removeEventListener("touchstart", handleInteraction);
    };

    document.addEventListener("click", handleInteraction);
    document.addEventListener("touchstart", handleInteraction);

    return () => {
      document.removeEventListener("click", handleInteraction);
      document.removeEventListener("touchstart", handleInteraction);
    };
  }, [audioContextError, setVisualizerAnalyserNode]);

  // Load saved volume
  useEffect(() => {
    if (savedVolume !== undefined && audioRef.current) {
      audioRef.current.volume = savedVolume;
      setVolume(savedVolume);
      console.log("=== useAudioPlayer: Loaded saved volume:", savedVolume);
    }
  }, [savedVolume, setVolume]);

  // Apply volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      saveVolumeDebounced(volume);
    }
  }, [volume, saveVolumeDebounced]);

  // Apply equalizer settings
  useEffect(() => {
    if (!isAudioContextInitialized || !audioContextRef.current) return;

    equalizerFiltersRef.current.forEach((filter, index) => {
      const gainValue = equalizer.enabled ? equalizer.bands[index] : 0;
      filter.gain.setValueAtTime(gainValue, audioContextRef.current!.currentTime);
    });
  }, [equalizer, isAudioContextInitialized]);

  // Handle track changes with robust error handling
  useEffect(() => {
    if (!currentTrack || !audioRef.current || audioContextError) {
      return;
    }

    // Prevent race conditions
    if (isSettingUpTrack || currentTrackIdRef.current === currentTrack.id) {
      return;
    }

    const setupTrack = async () => {
      try {
        setIsSettingUpTrack(true);
        currentTrackIdRef.current = currentTrack.id;
        console.log("=== useAudioPlayer: Setting up track:", currentTrack.title);

        const audio = audioRef.current!;
        const wasPlaying = !audio.paused;

        // Pause current playback
        if (!audio.paused) {
          audio.pause();
        }

        // Resume AudioContext if needed
        if (audioContextRef.current?.state === "suspended") {
          await audioContextRef.current.resume();
        }

        // Determine audio source
        let audioSource: string;
        if (currentTrack.source === "local" && currentTrack.blobData) {
          audioSource = URL.createObjectURL(new Blob([currentTrack.blobData], { type: "audio/mpeg" }));
        } else if (currentTrack.url) {
          audioSource = currentTrack.url;
        } else {
          throw new Error("No valid audio source found");
        }

        // Set new source
        audio.src = audioSource;
        audio.currentTime = 0;

        // Wait for metadata to be loaded
        await new Promise<void>((resolve, reject) => {
          const timeoutId = setTimeout(() => {
            reject(new Error("Metadata loading timeout"));
          }, 10000);

          const handleCanPlay = () => {
            clearTimeout(timeoutId);
            audio.removeEventListener("canplay", handleCanPlay);
            audio.removeEventListener("error", handleError);
            resolve();
          };

          const handleError = (e: Event) => {
            clearTimeout(timeoutId);
            audio.removeEventListener("canplay", handleCanPlay);
            audio.removeEventListener("error", handleError);
            reject(new Error("Audio loading failed"));
          };

          audio.addEventListener("canplay", handleCanPlay);
          audio.addEventListener("error", handleError);

          // Force load
          audio.load();
        });

        // Analyze track for haptics if it's a local file
        if (audioContextRef.current && currentTrack.blobData && currentTrack.source === "local") {
          try {
            const audioBuffer = await audioContextRef.current.decodeAudioData(currentTrack.blobData.slice(0));
            let preset = "pop"; // default

            if (currentTrack.genre) {
              const genreNormalized = currentTrack.genre.toLowerCase().trim();
              const presetKeys = Object.keys(DEFAULT_PRESETS);
              if (presetKeys.includes(genreNormalized)) {
                preset = genreNormalized;
              }
            }

            await analyzeTrack(audioBuffer, currentTrack.id, preset);
          } catch (error) {
            console.warn("=== useAudioPlayer: Haptic analysis failed:", error);
          }
        }

        // Reset progress
        setProgress(0);
        setCurrentTime(0);

        // Resume playback if it was playing before
        if (wasPlaying || isPlaying) {
          await audio.play();
          setIsPlaying(true);
        }

        console.log("=== useAudioPlayer: Track setup completed:", currentTrack.title);
      } catch (error) {
        console.error("=== useAudioPlayer: Track setup failed:", error);
        setIsPlaying(false);
        currentTrackIdRef.current = null;
      } finally {
        setIsSettingUpTrack(false);
      }
    };

    setupTrack();

    // Cleanup blob URLs when track changes
    return () => {
      if (audioRef.current?.src.startsWith("blob:")) {
        URL.revokeObjectURL(audioRef.current.src);
      }
    };
  }, [
    currentTrack,
    isPlaying,
    setIsPlaying,
    setProgress,
    setCurrentTime,
    analyzeTrack,
    audioContextError,
    isSettingUpTrack,
  ]);

  // Handle play/pause synchronization
  useEffect(() => {
    if (!audioRef.current || isSettingUpTrack) return;

    const audio = audioRef.current;

    const syncPlayback = async () => {
      try {
        if (isPlaying && audio.paused) {
          // Resume AudioContext if needed
          if (audioContextRef.current?.state === "suspended") {
            await audioContextRef.current.resume();
          }
          await audio.play();
        } else if (!isPlaying && !audio.paused) {
          audio.pause();
        }
      } catch (error) {
        console.error("=== useAudioPlayer: Playback sync failed:", error);
        setIsPlaying(false);
      }
    };

    syncPlayback();
  }, [isPlaying, isSettingUpTrack, setIsPlaying]);

  // Audio event handlers
  useEffect(() => {
    if (!audioRef.current) return;

    const audio = audioRef.current;

    const handleTimeUpdate = () => {
      if (!audio.duration) return;
      const progress = (audio.currentTime / audio.duration) * 100;
      setProgress(progress);
      setCurrentTime(audio.currentTime);
      provideFeedback(audio.currentTime);
    };

    const handleEnded = () => {
      console.log("=== useAudioPlayer: Track ended");
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
      playNext();
    };

    const handlePlay = () => {
      console.log("=== useAudioPlayer: Audio started playing");
      setIsPlaying(true);
    };

    const handlePause = () => {
      console.log("=== useAudioPlayer: Audio paused");
      if (!isSettingUpTrack) {
        setIsPlaying(false);
      }
    };

    const handleError = (e: Event) => {
      console.error("=== useAudioPlayer: Audio error:", e);
      setIsPlaying(false);
    };

    const handleLoadedMetadata = () => {
      console.log("=== useAudioPlayer: Metadata loaded");
      setProgress(0);
      setCurrentTime(0);
    };

    // Add event listeners
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("error", handleError);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("error", handleError);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, [setProgress, setCurrentTime, setIsPlaying, playNext, provideFeedback, isSettingUpTrack]);

  // Seek functionality
  const seekTo = useCallback(
    (time: number) => {
      if (!audioRef.current?.duration) return;

      const clampedTime = Math.max(0, Math.min(time, audioRef.current.duration));
      audioRef.current.currentTime = clampedTime;
      setCurrentTime(clampedTime);
      console.log("=== useAudioPlayer: Seeked to:", clampedTime);
    },
    [setCurrentTime]
  );

  const seekByPercentage = useCallback(
    (percentage: number) => {
      if (!audioRef.current?.duration) return;

      const time = (percentage / 100) * audioRef.current.duration;
      seekTo(time);
    },
    [seekTo]
  );

  // Add spacing adjustment for player
  useElementSpacing({
    selector: "main",
    property: "paddingBottom",
    condition: !!currentTrack,
    spacing: "90px",
    mobileSpacing: "140px",
  });

  // Cleanup
  useEffect(() => {
    return () => {
      if (volumeSaveTimeoutRef.current) {
        clearTimeout(volumeSaveTimeoutRef.current);
      }
    };
  }, []);

  return {
    audioRef,
    seekTo,
    seekByPercentage,
    isLoading: isSettingUpTrack,
    visualizerAnalyserNode: visualizerAnalyserRef.current,
    animateVolume,
  };
};
